import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  CircularProgress,
} from '@mui/material';
import { WhatsappLogoIcon, PhoneCallIcon } from '@phosphor-icons/react';
import ReusableModal from '../common/Modal';

interface AgentChannelSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (data: {
    channel: 'whatsapp' | 'voice';
    numbers?: string[];
    whatsappPhoneNumberId?: string;
    whatsappToken?: string;
    whatsappVerifyToken?: string;
  }) => void;
  loading?: boolean;
}

const AgentChannelSelectionModal = ({
  open,
  onClose,
  onContinue,
  loading = false,
}: AgentChannelSelectionModalProps) => {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState<'whatsapp' | 'voice' | null>(null);

  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState('');
  const [whatsappToken, setWhatsappToken] = useState('');
  const [whatsappVerifyToken, setWhatsappVerifyToken] = useState('');

  const handleNext = () => {
    if (selected === 'voice') {
      onContinue({ channel: 'voice' });
    } else if (selected === 'whatsapp') {
      setStep(2);
    }
  };

  const handleWhatsAppContinue = () => {
    if (!whatsappPhoneNumberId || !whatsappToken || !whatsappVerifyToken)
      return;
    onContinue({
      channel: 'whatsapp',
      whatsappPhoneNumberId,
      whatsappToken,
      whatsappVerifyToken,
    });
  };

  const handlePrevious = () => {
    setStep(1);
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Choose Your Agent Channel"
      width="639px"
    >
      {step === 1 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              mb: 2,
              color: '#6B6F80',
              fontFamily: 'DM Sans',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              letterSpacing: '-0.14px',
            }}
          >
            How do you want your agent to interact with customers?
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box
                onClick={() => setSelected('whatsapp')}
                sx={{
                  border:
                    selected === 'whatsapp'
                      ? '1px solid #5A4CE6'
                      : '1px solid #E0E0E0',
                  borderRadius: '12px',
                  p: 3,
                  cursor: 'pointer',
                  textAlign: 'center',
                  background:
                    selected === 'whatsapp'
                      ? 'rgba(90, 76, 230, 0.10)'
                      : 'white',
                }}
              >
                <Box
                  sx={{
                    color: 'primary.main',
                    bgcolor: '#F4F7FF',
                    p: 0.5,
                    height: '44px',
                    width: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                >
                  <WhatsappLogoIcon
                    size={32}
                    color="#6C63FF"
                    weight="duotone"
                  />
                </Box>
                <Typography
                  sx={{
                    color: '#302B3F',
                    fontFamily: 'DM Sans',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    mt: 1,
                  }}
                >
                  WhatsApp Chat Agent
                </Typography>
                <Typography
                  sx={{
                    color: '#6B6F80',
                    textAlign: 'center',
                    fontFamily: 'DM Sans',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.12px',
                  }}
                >
                  Chat-based support via your business number
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                onClick={() => setSelected('voice')}
                sx={{
                  border:
                    selected === 'voice'
                      ? '1px solid #5A4CE6'
                      : '1px solid #E0E0E0',
                  borderRadius: '12px',
                  p: 3,
                  cursor: 'pointer',
                  textAlign: 'center',
                  background:
                    selected === 'voice' ? 'rgba(90, 76, 230, 0.10)' : 'white',
                }}
              >
                <Box
                  sx={{
                    color: 'primary.main',
                    bgcolor: '#F4F7FF',
                    p: 0.5,
                    height: '44px',
                    width: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                >
                  <PhoneCallIcon size={32} color="#6C63FF" weight="duotone" />
                </Box>
                <Typography
                  sx={{
                    color: '#302B3F',
                    fontFamily: 'DM Sans',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    mt: 1,
                  }}
                >
                  Voice Call Agent
                </Typography>
                <Typography
                  sx={{
                    color: '#6B6F80',
                    textAlign: 'center',
                    fontFamily: 'DM Sans',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.12px',
                  }}
                >
                  Phone-based conversations with your customers
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                width: 'fit-content',
                height: '34px',
                borderRadius: '8px',
                padding: '8px 16px',
              }}
              disabled={!selected}
              onClick={handleNext}
            >
              {selected === 'voice' ? (
                loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Continue to Agent Setup'
                )
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Box>
      )}

      {step === 2 && selected === 'whatsapp' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Typography
            sx={{
              mb: 2,
              textAlign: 'center',
              color: '#6B6F80',
              fontFamily: 'DM Sans',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              letterSpacing: '-0.14px',
            }}
          >
            We just need a few details to connect your WhatsApp account
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: '480px', display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  width: '159px',
                  color: '#6B6F80',
                  fontFamily: 'DM Sans',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  letterSpacing: '-0.14px',
                }}
              >
                WhatsApp Phone Number ID
              </Typography>
              <TextField
                placeholder="Enter WhatsApp Phone Number ID"
                value={whatsappPhoneNumberId}
                onChange={e => setWhatsappPhoneNumberId(e.target.value)}
                sx={{
                  fontSize: '14px',
                  height: '34px',
                  fontWeight: 400,
                  color: '#6B6F80',
                  mb: 2,
                  width: '321px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#6B6F80',
                  },
                }}
              />
            </Box>
            <Box sx={{ width: '480px', display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  width: '159px',
                  color: '#6B6F80',
                  fontFamily: 'DM Sans',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  letterSpacing: '-0.14px',
                }}
              >
                WhatsApp Token
              </Typography>
              <TextField
                placeholder="Enter WhatsApp Token"
                value={whatsappToken}
                onChange={e => setWhatsappToken(e.target.value)}
                sx={{
                  fontSize: '14px',
                  height: '34px',
                  fontWeight: 400,
                  color: '#6B6F80',
                  mb: 2,
                  width: '321px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#6B6F80',
                  },
                }}
              />
            </Box>
            <Box sx={{ width: '480px', display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  width: '159px',
                  color: '#6B6F80',
                  fontFamily: 'DM Sans',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  letterSpacing: '-0.14px',
                }}
              >
                WhatsApp Verify Token
              </Typography>
              <TextField
                placeholder="Enter WhatsApp Verify Token"
                value={whatsappVerifyToken}
                onChange={e => setWhatsappVerifyToken(e.target.value)}
                sx={{
                  fontSize: '14px',
                  height: '34px',
                  fontWeight: 400,
                  color: '#6B6F80',
                  mb: 2,
                  width: '321px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    height: '40px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#6B6F80',
                  },
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Button
              variant="outlined"
              onClick={handlePrevious}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                width: 'fit-content',
                height: '34px',
                borderRadius: '8px',
                padding: '8px 16px',
              }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              disabled={
                !whatsappPhoneNumberId ||
                !whatsappToken ||
                !whatsappVerifyToken ||
                loading
              }
              onClick={handleWhatsAppContinue}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                width: 'fit-content',
                height: '34px',
                borderRadius: '8px',
                padding: '8px 16px',
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Continue to Agent Setup'
              )}
            </Button>
          </Box>
        </Box>
      )}

      {step === 2 && selected === 'voice' && (
        <Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Button variant="outlined" onClick={handlePrevious}>
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={() => onContinue({ channel: 'voice' })}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Continue to Agent Setup'
              )}
            </Button>
          </Box>
        </Box>
      )}
    </ReusableModal>
  );
};

export default AgentChannelSelectionModal;
